import { useCallback, useMemo, useState, useEffect } from "react";
import { Form, message, Select, Tooltip } from "antd";
import DatatableAntd from "components/datatable-antd";
import { MdDownload } from "react-icons/md";
import styled from "styled-components";
import columns from "../columns";
import Pagination from "./Pagination";
import { useFilters } from "core/features/filters/store";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import { useGetRelatorios } from "../hooks/useGetRelatorios";
import { useRelatorioAnaliticoExportXLS } from "../../../hooks/useRelatorio";
import useRelatorioContext from "../../../hooks/useRelatorioContext";
import { Loader } from "./Loader";
import { SELECT_POPUP_Z_INDEX } from "../utils";

type Props = {
  filtrosAvancados?: FiltrosAvancado;
  permission: boolean;
};

function Operacional({ filtrosAvancados, permission }: Props) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const filtersAdvanced = useFilters((s) => s.filters.filtersAdvanced);
  const expandScreen = useRelatorioContext((s) => s.expandScreen);
  const updateField = useFilters((s) => s.updateField);
  const { queryParams, setQueryParams } = useRelatorioContext();
  const { listRelatorios, relatorioAnaliticoParams, isFetching, isLoading } = useGetRelatorios();
  const { mutateAsync: exportAnaliticoXls, isLoading: exporting } =
    useRelatorioAnaliticoExportXLS();
  const { Item } = Form;

  const resHeader = listRelatorios?.headers?.paginate ? JSON.parse(listRelatorios?.headers?.paginate) : {
    currentPage: 0,
    resultsPerPage: 0,
    totalCount: 0
  };

  const tableColumns = useMemo(() => columns(), []);

  useEffect(() => {
    setQueryParams({
      Page: page,
      Size: size
    })
  }, [page, size]);

  const handleExportExcel = useCallback(async () => {
    const p = relatorioAnaliticoParams;
    if (!p?.UnidadeId || !p?.SafraId || !p?.SafraPeriodoIds) {
      message.warning(
        "Selecione fazenda, safra e período de produção para exportar."
      );
      return;
    }
    try {
      const blob = await exportAnaliticoXls(p);
      const objectUrl = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "relatorio-analitico-operacional.xls";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      message.success("Exportação concluída.");
    } catch {
      message.error("Não foi possível exportar o relatório.");
    }
  }, [exportAnaliticoXls, relatorioAnaliticoParams]);

  function obterTextoPaginacao(paginaAtual: number, itensPorPagina: number, totalRegistros: number) {
    if (totalRegistros === 0) {
      return 'Mostrando 0 - 0 de 0 registros';
    }

    const inicio = (paginaAtual - 1) * itensPorPagina + 1;
    const fim = Math.min(paginaAtual * itensPorPagina, totalRegistros);

    return `Mostrando ${inicio} - ${fim} de ${totalRegistros} ${totalRegistros === 1 ? 'registro' : 'registros'}`;
  }

  function getDefaultValue() {
    const filtro = filtersAdvanced?.safraPeriodo;
    const filtros = filtrosAvancados?.safraPeriodos;
    if (!filtros?.some(f => f.safraPeriodoId === filtro)) {
      return undefined;
    }
    return filtro;
  }

  return (
    <>
      <Content>
        <div style={{ display: "flex", gap: "12px" }}>
          <div style={{ width: 280 }}>
            <Item
              label="Período de Produção"
              name="periodoProducao"
              rules={[{ required: true, message: "Período de Produção é obrigatório" }]}
            >
              <SelectAntd
                onChange={(dataValue: any) => {
                  queueMicrotask(() => {
                    updateField("filtersAdvanced", {
                      ...filtersAdvanced,
                      safraPeriodo: dataValue,
                    });
                  });
                }}
                placeholder="Período de Produção"
                disabled={!permission}
                defaultValue={getDefaultValue()}
                options={(filtrosAvancados?.safraPeriodos || [])
                  ?.map((item: any) => ({
                    label: item.nome,
                    value: item.safraPeriodoId,
                  }))}
              />
            </Item>
          </div>
          <div style={{ width: 280 }}>
            <Item label="Classe de Operação" name="OperacaoClasseIds">
              <SelectAntd
                placeholder="Classe de Operação"
                allowClear
                showSearch
                mode="multiple"
                maxTagCount="responsive"
                defaultValue={filtersAdvanced?.classeOperacao}
                optionFilterProp="label"
                onChange={(dataValue: any) => {
                  queueMicrotask(() => {
                    setQueryParams({
                      ...queryParams,
                      OperacaoClasseIds: dataValue
                    });
                  });
                }}
                options={(filtrosAvancados?.operacaoClasses || [])
                  ?.map((item: any) => ({
                    label: item.descricao,
                    value: item.operacaoClasseId,
                  }))}
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip
                    overlayStyle={
                      expandScreen ? { zIndex: SELECT_POPUP_Z_INDEX } : undefined
                    }
                    title={omittedValues.map((v) => v.label).join(", ")}
                    placement="top"
                  >
                    +{omittedValues.length} itens
                  </Tooltip>
                )}
              />
            </Item>
          </div>
        </div>
        <ButtonTypeExport
          type="button"
          disabled={exporting || !permission}
          onClick={handleExportExcel}
        >
          <MdDownload size={20} />{" "}
          {exporting ? "Exportando…" : "Exportar para Excel"}
        </ButtonTypeExport>
      </Content>
      {
        permission ? (
          <>
            {
              isLoading || isFetching ? <Loader /> : (
                <>
                  <TableWrapper>
                    <RoundedTable
                      style={{ marginBottom: 16 }}
                      columns={tableColumns}
                      dataSource={listRelatorios?.data || []}
                      scroll={{ x: 2400, y: "calc(100vh - 300px)" }}
                      pagination={false}
                    />
                  </TableWrapper>

                </>
              )
            }
            <ContentFooter>
              <div>
                <span>{obterTextoPaginacao(page, size, resHeader?.totalCount || 0)}</span>
              </div>
              <Pagination
                page={page}
                size={size}
                total={resHeader?.totalCount || 0}
                onChange={(p, s) => {
                  setPage(p);
                  setSize(s);
                }}
              />
            </ContentFooter>

          </>
        ) : (
          <h2 style={{ textAlign: "center", padding: 15 }}>Você não tem permissão para visualizar.</h2>
        )
      }
    </>
  )
}

const SelectAntd = styled(Select)`
  width: 100% !important;
  
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    padding: 4px 11px !important;
  }

  .ant-form-item-label {
    text-align: left !important;
  }

   &.ant-select-multiple > .ant-select-selector {
    border: 1px solid #d9d9d9 !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple > .ant-select-selector:hover {
    border: 1px solid #40a9ff !important;
     box-shadow: none !important;
     outline: none !important;
  }
  &.ant-select-multiple.ant-select-open > .ant-select-selector {
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    border-right-width: 1px !important;
    outline: 0;
  }
`;


const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

    .ant-form-item-required::before {
        content: none !important;
    }

  .ant-form-item-required::after {
    display: inline-block !important;
    margin-left: 4px !important;
    color: red;
    font-size: 14px !important;
    line-height: 1 !important;
    content: "*" !important;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
`;

export const ContentFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
`

const RoundedTable = styled(DatatableAntd)`
  height: 100%;
  overflow-x: auto;

  .ant-table {
    border-radius: 8px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 8px;
    overflow: hidden;
  }

  .ant-table-content {
    overflow-x: auto !important;
    overflow-y: hidden !important;
  }

  .ant-table-body {
    max-height: calc(100vh - 280px);
    overflow-y: auto !important;
    overflow-x: auto !important;
  }

`;

const ButtonType = styled.button<{ active?: boolean }>`
  /* padding: 10px 20px; */
  height: 40px;
  padding: 7px 20px;
  border: 2px solid #e0e0e0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  transition: all 0.3s;

  ${props => !props.active && `
    &:hover {
      border-color: #2196f3;
      background: #f0f8ff;
      color: #2196f3;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(33, 150, 243, 0.2);
    }
  `}

  ${props => props.active && `
    background: #12619C;
    color: white;
    border-color: #1976d2;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
  `}

  &:disabled {
    background: #ddd !important;
    cursor: not-allowed !important;
    color: #666;
    opacity: 0.5;
    border: none;

    &:hover {
      background: #ddd !important;
      box-shadow: none !important;
      color: #666;
    }
  }
`;

const ButtonTypeExport = styled(ButtonType)`
  background-color: #009157;
  color: #fff;

  &:hover {
    background-color: #009157;
    color: #fff;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  /* overflow: hidden; */
`;



export default Operacional;